/**
 * <ActivitiesSkeleton> — la forme de l'onglet « Activités » pendant son premier chargement.
 *
 * Le titre de la page est le vrai, et la place des deux gestes de la barre de titre est gardée
 * (la ligne ne change pas de hauteur à leur arrivée). Puis le pli « Faites » (le cas d'un enfant
 * qui a déjà travaillé), l'intitulé « À faire » et sa carte-liste : quatre lignes d'activité —
 * la chip de matière, le titre, le chapitre, la chip de durée — et « Générer une activité ».
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Title } from "../../atoms/typography/Title"
import { ActivityRowSkeleton } from "../../molecules/ActivityRow/ActivityRowSkeleton"
import { CardList, CardListRow } from "../../molecules/CardList"
import { REFRESH_BUTTON_SIZE } from "../../molecules/RefreshButton"

export type ActivitiesSkeletonProps = {
    /**
     * La place du lien « Sélectionner », à droite de « À faire ». Défaut `true` (le parent) ;
     * l'élève, qui ne sélectionne pas, la retire.
     */
    selectable?: boolean
    /**
     * La place des gestes de la barre de titre (relecture, menu des archives). Défaut `true` (le
     * parent) ; l'élève, qui n'en a aucun, la retire.
     */
    titleActions?: boolean
}

export function ActivitiesSkeleton(props: ActivitiesSkeletonProps) {
    const { selectable = true, titleActions = true } = props

    return (
        <Stack gap="6">
            <Row justify="between" align="center">
                <Title size="text-2xl">Activités</Title>
                {titleActions && <View style={styles.titleActions} />}
            </Row>

            <Row justify="between" align="center">
                <Row gap="2" align="center">
                    <SectionLabel>Faites</SectionLabel>
                    <Skeleton shape="chip" width={28} />
                </Row>
                <Skeleton textSize="text-md" width={72} />
            </Row>

            <Stack gap="3">
                <Row justify="between" align="center">
                    <Row gap="2" align="center">
                        <SectionLabel>À faire</SectionLabel>
                        <Skeleton shape="chip" width={28} />
                    </Row>
                    {selectable && <Skeleton textSize="text-sm" width={80} />}
                </Row>
                <CardList>
                    <ActivityRowSkeleton titleWidth={170} />
                    <ActivityRowSkeleton titleWidth={120} />
                    <ActivityRowSkeleton titleWidth={150} />
                    <ActivityRowSkeleton titleWidth={130} />
                    <CardListRow>
                        <Skeleton textSize="text-md" width={150} />
                    </CardListRow>
                </CardList>
            </Stack>
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    // La cible du plus haut geste de la barre de titre : la ligne ne change pas de hauteur à leur arrivée.
    titleActions: { width: REFRESH_BUTTON_SIZE, height: REFRESH_BUTTON_SIZE },
}))
